import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../utils/translations';
import { getAlbanianCityOptions } from '../../constants/albanianCities';
import { isValidInstagram } from '../../utils/instagramHelpers';
import { uploadAPI, agentsAPI } from '../../services/api';
import { createCenterCropPreview } from '../../utils/agentPhotoCrop';
import { AGENT_PHOTO_UPLOAD } from '../../constants/agentPhotoSpecs';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import '../PropertyFormModal/PropertyFormModal.css';
import './AgentFormModal.css';

const TITLE_OPTIONS = [
  'Real Estate Agent',
  'Broker',
  'Office Manager',
  'Estate Office',
];

const AgentFormModal = ({ isOpen, onClose, agent, onSave }) => {
  const { t } = useTranslation();
  const isEditMode = !!agent;

  const [formData, setFormData] = useState({
    name: '',
    title: 'Real Estate Agent',
    username: '',
    instagram: '',
    email: '',
    mobile: '',
    city: 'Tirana',
    image: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [passwordData, setPasswordData] = useState({ password: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;

    if (agent) {
      setFormData({
        name: agent.name || '',
        title: agent.title || 'Real Estate Agent',
        username: agent.username || '',
        instagram: agent.instagram || '',
        email: agent.email || '',
        mobile: agent.mobile || '',
        city: agent.city || 'Tirana',
        image: agent.image || '',
      });
      setPhotoPreview(agent.image ? resolveMediaUrl(agent.image) : '');

      const loadUsername = async () => {
        if (agent.username || !agent.adminId) return;

        try {
          const fullAgent = await agentsAPI.getByIdForManage(agent.id);
          if (cancelled || !fullAgent?.username) return;
          setFormData((prev) => ({ ...prev, username: fullAgent.username }));
        } catch (loadError) {
          console.warn('Could not load agent username:', loadError);
        }
      };

      loadUsername();
    } else {
      setFormData({
        name: '',
        title: 'Real Estate Agent',
        username: '',
        instagram: '',
        email: '',
        mobile: '',
        city: 'Tirana',
        image: '',
      });
      setPhotoPreview('');
    }

    setPhotoFile(null);
    setPasswordData({ password: '' });
    setError('');

    return () => {
      cancelled = true;
    };
  }, [agent, isOpen]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setPhotoFile(null);
      setPhotoPreview(formData.image ? resolveMediaUrl(formData.image) : '');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError(t('admin.register.photoInvalid') || 'Please upload an image file');
      return;
    }

    if (file.size > AGENT_PHOTO_UPLOAD.maxFileSizeMb * 1024 * 1024) {
      setError(t('admin.register.photoTooLarge') || `Photo must be under ${AGENT_PHOTO_UPLOAD.maxFileSizeMb}MB`);
      return;
    }

    try {
      const preview = await createCenterCropPreview(file);
      setPhotoFile(file);
      setPhotoPreview(preview);
      setError('');
    } catch (photoError) {
      setError(t('admin.register.photoInvalid') || 'Please upload an image file');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError(t('admin.agentForm.nameRequired') || 'Name is required');
      return;
    }

    if (!formData.instagram.trim() || !isValidInstagram(formData.instagram)) {
      setError(t('admin.agentForm.instagramRequired') || 'Valid Instagram is required');
      return;
    }

    const hasLoginAccount = isEditMode && (agent?.hasLogin || agent?.adminId);

    if (hasLoginAccount) {
      if (!formData.username.trim()) {
        setError(t('admin.agentForm.usernameRequired') || 'Username is required');
        return;
      }

      if (formData.username.trim().length < 3) {
        setError(t('admin.agentForm.usernameTooShort') || 'Username must be at least 3 characters');
        return;
      }
    }

    const wantsPasswordChange = passwordData.password.trim();
    if (isEditMode && wantsPasswordChange) {
      if (!hasLoginAccount) {
        setError(t('admin.agentForm.noLoginAccount') || 'This agent does not have a login account');
        return;
      }

      if (passwordData.password.length < 6) {
        setError(t('admin.agentForm.passwordTooShort') || 'Password must be at least 6 characters');
        return;
      }
    }

    try {
      let imageUrl = formData.image;

      if (photoFile) {
        setUploadingPhoto(true);
        const uploadResult = await uploadAPI.uploadAgentPhoto(photoFile);
        imageUrl = uploadResult.url;
      }

      if (!imageUrl && !isEditMode) {
        setError(t('admin.register.photoRequired') || 'Agent photo is required');
        return;
      }

      await onSave({
        name: formData.name,
        title: formData.title,
        instagram: formData.instagram,
        email: formData.email,
        mobile: formData.mobile,
        city: formData.city,
        image: imageUrl,
        ...(hasLoginAccount ? { username: formData.username.trim() } : {}),
        ...(wantsPasswordChange ? { password: passwordData.password } : {}),
      });
    } catch (submitError) {
      setError(submitError.message || t('admin.agentForm.saveError') || 'Failed to save agent');
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content agent-form-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {isEditMode
              ? t('admin.agentForm.editAgent') || 'Edit Agent'
              : t('admin.agentForm.addAgent') || 'Add Agent'}
          </h2>
          <button type="button" className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="property-form">
          {error && <p className="agent-form-error">{error}</p>}

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.register.name')} *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>{t('admin.register.type') || 'Type'} *</label>
              <select name="title" value={formData.title} onChange={handleChange} required>
                {TITLE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
                {!TITLE_OPTIONS.includes(formData.title) && formData.title && (
                  <option value={formData.title}>{formData.title}</option>
                )}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.register.instagram')} *</label>
              <input type="text" name="instagram" value={formData.instagram} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>{t('admin.register.email')}</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.register.mobile')}</label>
              <input type="text" name="mobile" value={formData.mobile} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>{t('admin.register.city')}</label>
              <select name="city" value={formData.city} onChange={handleChange}>
                {getAlbanianCityOptions().map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
          </div>

          {isEditMode && (agent?.hasLogin || agent?.adminId) && (
            <div className="agent-form-section">
              <h3>{t('admin.agentForm.accountSection') || 'Login Account'}</h3>
              <div className="form-group">
                <label>{t('username') || 'Username'} *</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  autoComplete="username"
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('admin.agentForm.newPassword') || 'New Password'}</label>
                <input
                  type="password"
                  name="password"
                  value={passwordData.password}
                  onChange={handlePasswordChange}
                  placeholder={t('admin.agentForm.newPasswordPlaceholder') || 'Leave blank to keep current password'}
                  autoComplete="new-password"
                />
              </div>
              <p className="agent-form-hint">
                {t('admin.agentForm.passwordHint') || 'Fill this field only if you want to change the login password.'}
              </p>
            </div>
          )}

          {isEditMode && !agent?.hasLogin && !agent?.adminId && (
            <p className="agent-form-hint">
              {t('admin.agentForm.noLoginAccount') || 'This agent does not have a login account.'}
            </p>
          )}

          <div className="form-group">
            <label>{t('admin.register.photo')}{!isEditMode ? ' *' : ''}</label>
            {photoPreview && (
              <div className="image-preview agent-photo-preview">
                <img src={photoPreview} alt={formData.name || 'Agent'} />
              </div>
            )}
            <label className="file-upload-label">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="file-input"
                disabled={uploadingPhoto}
              />
              <span className="file-upload-button">
                {uploadingPhoto
                  ? t('admin.form.uploading') || 'Uploading...'
                  : t('admin.register.choosePhoto') || 'Choose Photo'}
              </span>
            </label>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              {t('admin.form.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={uploadingPhoto}>
              {isEditMode ? t('admin.form.update') : t('admin.form.add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentFormModal;
